import { Link, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getIsAdmin } from "@/lib/payments.functions";
import { NotificationBell } from "@/components/NotificationBell";
import {
  LayoutDashboard,
  Calendar,
  Briefcase,
  Wallet,
  Coins,
  FileText,
  Star,
  Bookmark,
  Users,
  BarChart3,
  Radio,
  LayoutTemplate,
  Repeat,
  Building2,
  History,
  Trophy,
  User,
  ClipboardList,
} from "lucide-react";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  match?: "exact" | "prefix";
};

const BARTENDER_NAV: NavItem[] = [
  { to: "/bartender", label: "Overview", icon: LayoutDashboard, match: "exact" },
  { to: "/bartender/engagements", label: "Engagements", icon: Briefcase },
  { to: "/bartender/wallet", label: "Wallet", icon: Wallet },
  { to: "/bartender/tips", label: "Tips", icon: Coins },
  { to: "/bartender/availability", label: "Availability", icon: Calendar },
  { to: "/bartender/saved", label: "Saved", icon: Bookmark },
  { to: "/bartender/documents", label: "Documents", icon: FileText },
  { to: "/bartender/reviews", label: "Reviews", icon: Star },
  { to: "/bartender/goals", label: "Goals", icon: Trophy },
];

const VENUE_NAV: NavItem[] = [
  { to: "/venue", label: "Overview", icon: LayoutDashboard, match: "exact" },
  { to: "/venue/live", label: "Live", icon: Radio },
  { to: "/venue/previous", label: "Previous", icon: History },
  { to: "/venue/crews", label: "Crews", icon: Users },
  { to: "/venue/templates", label: "Templates", icon: LayoutTemplate },
  { to: "/venue/series", label: "Series", icon: Repeat },
  { to: "/venue/group", label: "Group", icon: Building2 },
  { to: "/venue/insights", label: "Insights", icon: BarChart3 },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/admin", label: "Console", icon: LayoutDashboard, match: "exact" },
  { to: "/admin/applications", label: "Applications", icon: ClipboardList },
  { to: "/admin/appeals", label: "Appeals", icon: FileText },
];

export function ReserveHeader({
  role,
  payoutsEnabled,
}: {
  role?: string;
  payoutsEnabled?: boolean;
}) {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const checkAdmin = useServerFn(getIsAdmin);
  const [isAdmin, setIsAdmin] = useState(false);
  const [headerPayoutsEnabled, setHeaderPayoutsEnabled] = useState<boolean | undefined>(undefined);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    checkAdmin().then((r) => setIsAdmin(!!r.isAdmin)).catch(() => setIsAdmin(false));
  }, []);

  useEffect(() => {
    if (role !== "bartender" || payoutsEnabled !== undefined) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: u } = await supabase.auth.getUser();
        if (!u.user || cancelled) return;
        const { data } = await supabase
          .from("profiles")
          .select("stripe_payouts_enabled")
          .eq("id", u.user.id)
          .maybeSingle();
        if (!cancelled) setHeaderPayoutsEnabled(!!data?.stripe_payouts_enabled);
      } catch {
        if (!cancelled) setHeaderPayoutsEnabled(undefined);
      }
    })();
    return () => { cancelled = true; };
  }, [role, payoutsEnabled]);

  const effectivePayoutsEnabled = payoutsEnabled !== undefined ? payoutsEnabled : headerPayoutsEnabled;

  const handleSignOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    router.invalidate();
    navigate({ to: "/auth", replace: true });
  };

  // Choose which secondary nav to render. Admin routes get the admin strip.
  const isAdminRoute = pathname.startsWith("/admin");
  let secondary: NavItem[] = [];
  if (isAdminRoute && isAdmin) secondary = ADMIN_NAV;
  else if (role === "bartender") secondary = BARTENDER_NAV;
  else if (role === "venue") secondary = VENUE_NAV;

  const isActive = (item: NavItem) =>
    item.match === "exact" ? pathname === item.to : pathname === item.to || pathname.startsWith(item.to + "/");

  const homeHref = role === "venue" ? "/venue" : role === "bartender" ? "/bartender" : "/dashboard";

  return (
    <header className="bg-background text-foreground border-b border-border">
      {/* Brand row */}
      <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between gap-4">
        <Link to={homeHref} className="block shrink-0">
          <p className="tracking-wide-luxury text-accent text-xs">The Hospitality</p>
          <p className="font-display text-lg leading-tight">Reserve</p>
        </Link>

        <div className="flex items-center gap-4 sm:gap-5">
          {role && (
            <span className="hidden sm:inline text-[10px] tracking-wide-luxury uppercase text-muted-foreground">
              {role === "venue" ? "Venue" : role === "bartender" ? "Bartender" : role}
            </span>
          )}

          {role === "bartender" && effectivePayoutsEnabled === false && (
            <Link
              to="/bartender/payouts-setup"
              className="inline-flex items-center gap-1.5 rounded-sm bg-accent px-3 py-1.5 text-xs font-medium tracking-wide-luxury text-accent-foreground hover:bg-accent/90 transition-colors"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              Connect bank
            </Link>
          )}

          <Link to="/invoices" className="hidden md:inline text-xs tracking-wide-luxury text-muted-foreground hover:text-accent transition-colors">
            Invoices
          </Link>
          <Link to="/account" className="hidden md:inline-flex items-center gap-1.5 text-xs tracking-wide-luxury text-muted-foreground hover:text-accent transition-colors">
            <User className="h-3.5 w-3.5" aria-hidden="true" />
            Account
          </Link>

          <NotificationBell />

          {isAdmin && !isAdminRoute && (
            <Link to="/admin" className="text-xs tracking-wide-luxury text-accent hover:text-accent/80 transition-colors">
              Admin
            </Link>
          )}

          <button
            onClick={handleSignOut}
            className="px-3 py-1.5 text-xs tracking-wide-luxury border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Secondary role-based nav */}
      {secondary.length > 0 && (
        <nav className="border-t border-border bg-secondary/30">
          <div className="mx-auto max-w-7xl px-6">
            <ul className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
              {secondary.map((item) => {
                const active = isActive(item);
                const Icon = item.icon;
                return (
                  <li key={item.to} className="shrink-0">
                    <Link
                      to={item.to}
                      className={
                        "inline-flex items-center gap-2 border-b-2 px-3 py-3 text-xs tracking-wide-luxury uppercase transition-colors " +
                        (active
                          ? "border-accent text-foreground"
                          : "border-transparent text-muted-foreground hover:text-foreground hover:border-border")
                      }
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      )}
    </header>
  );
}
